import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Heart, Brain, Home, Calendar, Wallet, Users, ArrowRight, 
  Check, Star, ChevronDown, BookOpen, Wind, Target,
  Dumbbell, Shield, Sparkles, Send, Play
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIVON_LOGO = "https://static.prod-images.emergentagent.com/jobs/b293bd06-5db6-47e3-9bac-b35b794055ae/images/24e9f4cae61d982d9c454efb36258018ae47a7c7ef1d43817c3543666c110b4a.png";

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/50 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid={`faq-${question.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="font-medium text-foreground pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-40 pb-5' : 'max-h-0'}`}>
        <p className="text-muted-foreground leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

const FAQ_DATA = [
  {
    question: 'Vad kostar Livon?',
    answer: 'Livon kostar 299 kr som engångsbetalning. Du får 3 dagars gratis provperiod först.'
  },
  {
    question: 'Fungerar det på min mobil?',
    answer: 'Ja! Livon fungerar på alla enheter - mobil, surfplatta och dator. Du kan även installera appen på din hemskärm för snabb åtkomst.'
  },
  {
    question: 'Kan jag testa innan jag betalar?',
    answer: 'Absolut! Du får 3 dagars gratis provperiod med tillgång till alla funktioner. Ingen betalning krävs förrän provperioden är slut.'
  },
  {
    question: 'Hur säkra är mina uppgifter?',
    answer: 'Vi tar din integritet på allvar. All data är krypterad, vi säljer aldrig dina uppgifter, och du kan radera ditt konto och all data när som helst.'
  },
  {
    question: 'Vem driver Livon?',
    answer: 'Livon drivs av Expressa AB (org.nr 556940-4436), ett svenskt företag. Alla köp och avtal ingås med Expressa AB.'
  }
];

const FEATURES = [
  { icon: BookOpen, title: 'Dagbok & Reflektioner', description: 'Skriv ner dina tankar, följ ditt mående och odla tacksamhet varje dag.', color: 'bg-rose-100 text-rose-600', accent: 'rose' },
  { icon: Dumbbell, title: 'Träning & Hälsa', description: 'Professionella träningsprogram, måltidsplanering och hälsologg.', color: 'bg-emerald-100 text-emerald-600', accent: 'emerald' },
  { icon: Wind, title: 'Lugn & Meditation', description: 'Andningsövningar, guidade meditationer och fokusverktyg.', color: 'bg-sky-100 text-sky-600', accent: 'sky' },
  { icon: Home, title: 'Hem & Rutiner', description: 'Städschema, inköpslistor, morgon- och kvällsrutiner.', color: 'bg-amber-100 text-amber-600', accent: 'amber' },
  { icon: Target, title: 'Mål & Vanor', description: 'Sätt upp mål, bygg goda vanor och följ dina framsteg.', color: 'bg-violet-100 text-violet-600', accent: 'violet' },
  { icon: Wallet, title: 'Ekonomi & Karriär', description: 'Budget, sparmål, karriärguider och jobbsökning.', color: 'bg-teal-100 text-teal-600', accent: 'teal' },
  { icon: Users, title: 'Relationer', description: 'Kommunikation, parövningar, gränser och familj.', color: 'bg-pink-100 text-pink-600', accent: 'pink' },
  { icon: Heart, title: 'Självkännedom', description: 'Förstå dina mönster, triggers och bygg självkärlek.', color: 'bg-orange-100 text-orange-600', accent: 'orange' },
];

const STEPS = [
  { num: '1', title: 'Skapa konto', description: 'Registrera dig på under en minut. 3 dagars gratis provperiod - ingen betalning krävs.', color: 'from-sky-400 to-blue-600' },
  { num: '2', title: 'Utforska', description: 'Upptäck alla verktyg: dagbok, träning, rutiner, mål och mycket mer.', color: 'from-violet-400 to-purple-600' },
  { num: '3', title: 'Väx varje dag', description: 'Bygg goda vanor, följ dina framsteg och ta kontroll över ditt liv.', color: 'from-rose-400 to-pink-600' },
];

const TESTIMONIALS = [
  { name: 'Sofia', city: 'Stockholm', text: 'Livon har verkligen hjälpt mig få struktur i vardagen. Att ha dagbok, träning och rutiner på samma ställe gör stor skillnad.' },
  { name: 'Josef', city: 'Göteborg', text: 'Jag har testat många appar men ingen har samlat allt så bra som Livon. Budget, mål och träning — allt på ett ställe.' },
  { name: 'Anna', city: 'Malmö', text: 'Att kunna följa mitt mående och se mönster har varit en game changer. Bästa investeringen jag gjort i mig själv.' },
  { name: 'Marcus', city: 'Uppsala', text: 'Träningsprogrammen och måltidsplaneringen har gjort det enkelt att hålla koll på min hälsa. Rekommenderar starkt!' },
  { name: 'Laura', city: 'Linköping', text: 'Dagboken och tacksamhetsövningarna har blivit en del av min morgonrutin. Känner mig mer medveten om hur jag mår.' },
  { name: 'Erik', city: 'Lund', text: 'Äntligen en app som samlar allt jag behöver — ekonomi, rutiner, mål och hälsa. Slipper ha tio olika appar.' },
];

const AVATAR_COLORS = [
  'from-rose-400 to-pink-500',
  'from-amber-400 to-orange-500',
  'from-emerald-400 to-teal-500',
  'from-sky-400 to-blue-500',
  'from-violet-400 to-purple-500',
  'from-pink-400 to-rose-500',
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [userReviews, setUserReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 5, text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(res => {
      if (res.data.reviews?.length) setUserReviews(res.data.reviews);
    }).catch(() => {});

    // Persist any referral code (?code=XYZ) so it survives registration/login
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('code');
    if (urlCode) {
      localStorage.setItem('livon_referral_code', urlCode);
    }
  }, []);

  const handleSubmitReview = async () => {
    const text = (reviewForm.text || '').trim();
    if (text.length < 5) {
      toast.error('Recensionen måste vara minst 5 tecken.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      // Save draft and redirect to login
      try {
        sessionStorage.setItem('livon_pending_review', JSON.stringify(reviewForm));
      } catch (e) { /* ignore */ }
      toast.info('Logga in först — vi sparar din recension åt dig.');
      navigate('/auth?next=/&review=1');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, reviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmitted(true);
      setShowReviewForm(false);
      setReviewForm({ name: '', rating: 5, text: '' });
      try { sessionStorage.removeItem('livon_pending_review'); } catch (e) { /* ignore */ }
      toast.success('Tack för din recension! Den visas så snart vi godkänt den.');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401 || status === 403) {
        // Token invalid/expired
        try { sessionStorage.setItem('livon_pending_review', JSON.stringify(reviewForm)); } catch (e) { /* ignore */ }
        toast.info('Din session har gått ut. Logga in igen.');
        navigate('/auth?next=/&review=1');
      } else {
        toast.error(detail || 'Kunde inte skicka recensionen. Försök igen om en stund.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Restore pending review draft after login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('review') === '1' && localStorage.getItem('token')) {
      try {
        const draft = sessionStorage.getItem('livon_pending_review');
        if (draft) {
          const parsed = JSON.parse(draft);
          setReviewForm(parsed);
          setShowReviewForm(true);
          // Scroll to form after a tick
          setTimeout(() => {
            document.querySelector('[data-testid="review-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }
      } catch (e) { /* ignore */ }
    }
  }, []);

  const allReviews = [
    ...TESTIMONIALS.map(t => ({ ...t, rating: 5 })),
    ...userReviews.map(r => ({ name: r.name, city: '', text: r.text, rating: r.rating }))
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navigation - logo and name centered */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-center gap-3">
          <div className="flex items-center gap-3">
            <img src={LIVON_LOGO} alt="Livon" className="h-10 w-auto" />
            <span className="font-heading text-3xl font-semibold tracking-tight">Livon<sup className="text-xs font-normal align-super">®</sup></span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 px-6 overflow-hidden">
        {/* Subtle neutral background accents */}
        <div className="absolute top-20 -left-32 w-96 h-96 bg-gradient-to-br from-slate-200/30 to-gray-200/20 rounded-full blur-3xl blob-1" />
        <div className="absolute top-40 -right-32 w-80 h-80 bg-gradient-to-br from-blue-100/20 to-slate-200/20 rounded-full blur-3xl blob-2" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-200/50 text-amber-700 text-sm font-medium mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4" />
            3 dagars gratis provperiod
          </div>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-light tracking-tight leading-[1.1] mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Ditt liv i <span className="text-gradient-vivid font-medium">en app</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Livon hjälper dig få struktur, bygga goda vanor och ta hand om ditt välmående. Dagbok, träning, ekonomi, relationer - allt på ett ställe.
          </p>
          
          <div className="flex flex-col items-center gap-3 mb-6 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <Button 
              onClick={() => navigate('/auth')}
              className="btn-cta rounded-full px-10 py-6 text-lg group shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              data-testid="get-started-btn"
            >
              Kom igång gratis
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              onClick={() => navigate('/auth')}
              className="rounded-full px-10 py-5 text-base bg-red-500 hover:bg-red-600 text-white shadow-md hover:shadow-lg transition-all duration-300"
              data-testid="hero-login-btn"
            >
              Logga in
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground animate-fade-in" style={{ animationDelay: '0.4s' }}>
            299 kr  ·  Engångsbetalning  ·  3 dagars gratis provperiod
          </p>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-10 border-y border-border/30 bg-slate-50/50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-10 text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {AVATAR_COLORS.slice(0, 5).map((color, i) => (
                  <div key={i} className={`w-8 h-8 rounded-full bg-gradient-to-br ${color} border-2 border-background`} />
                ))}
              </div>
              <span className="text-sm font-medium text-foreground">Hundratals användare</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-sm font-medium text-foreground">5/5 betyg</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-500" />
              <span className="text-sm font-medium text-foreground">GDPR-säker</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features - more colorful cards */}
      <section id="features" className="py-20 md:py-28 px-6" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest uppercase text-rose-500 font-medium mb-4">Allt du behöver</p>
            <h2 className="text-4xl md:text-5xl font-heading font-normal tracking-tight mb-4">
              En komplett verktygslåda
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Samla hela ditt liv på ett ställe. Från tankar och träning till hem och ekonomi.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((feature, index) => (
              <div 
                key={feature.title}
                className="group bg-card rounded-2xl p-6 border border-border/30 hover:shadow-lg hover:-translate-y-2 transition-all duration-300 opacity-0 animate-slide-up relative overflow-hidden"
                style={{ animationDelay: `${index * 0.08}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-current opacity-[0.04] rounded-bl-full transition-all duration-300 group-hover:opacity-[0.08] group-hover:w-32 group-hover:h-32" />
                <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works - colorful step indicators */}
      <section className="py-20 md:py-28 px-6 bg-gradient-to-b from-slate-50 to-white" data-testid="how-it-works-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest uppercase text-sky-500 font-medium mb-4">Så fungerar det</p>
            <h2 className="text-4xl md:text-5xl font-heading font-normal tracking-tight mb-4">
              Kom igång på 3 steg
            </h2>
            <p className="text-lg text-muted-foreground">
              Kom igång enkelt med Livon.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((step, index) => (
              <div key={step.num} className="text-center opacity-0 animate-slide-up" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} text-white flex items-center justify-center text-2xl font-heading font-bold mx-auto mb-5 shadow-lg`}>
                  {step.num}
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-3">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="reviews" className="py-20 md:py-28 px-6" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm tracking-widest uppercase text-amber-500 font-medium mb-4">Recensioner</p>
            <h2 className="text-4xl md:text-5xl font-heading font-normal tracking-tight">
              Vad våra användare säger
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allReviews.map((t, index) => (
              <div 
                key={`review-${index}`}
                className="bg-card rounded-2xl p-7 border border-border/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 opacity-0 animate-slide-up"
                style={{ animationDelay: `${index * 0.08}s` }}
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`w-4 h-4 ${i <= t.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/20'}`} />
                  ))}
                </div>
                <p className="text-foreground leading-relaxed mb-5">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${AVATAR_COLORS[index % AVATAR_COLORS.length]} flex items-center justify-center text-white font-bold text-sm`}>
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t.name}</p>
                    {t.city && <p className="text-xs text-muted-foreground">{t.city}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Leave a review */}
          <div className="mt-12 text-center">
            {submitted ? (
              <p className="text-emerald-600 font-medium" data-testid="review-thank-you">Tack för din recension! Den granskas innan den publiceras.</p>
            ) : showReviewForm ? (
              <div className="max-w-md mx-auto bg-card rounded-2xl p-6 border border-border/30 text-left shadow-lg" data-testid="review-form">
                <h3 className="font-heading text-xl font-semibold mb-2">Lämna en recension</h3>
                {!localStorage.getItem('token') && (
                  <p className="text-xs text-muted-foreground mb-4 bg-secondary/40 rounded-lg px-3 py-2" data-testid="review-login-notice">
                    För att förhindra spam behöver du logga in när du klickar på <strong>Skicka</strong>. Vi sparar din text åt dig.
                  </p>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Ditt namn (valfritt)</label>
                    <input
                      type="text"
                      value={reviewForm.name}
                      onChange={e => setReviewForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Förnamn"
                      className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid="review-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Betyg</label>
                    <div className="flex gap-1">
                      {[1,2,3,4,5].map(i => (
                        <button
                          key={i}
                          onClick={() => setReviewForm(p => ({ ...p, rating: i }))}
                          className="p-1"
                          data-testid={`review-star-${i}`}
                        >
                          <Star className={`w-6 h-6 transition-colors ${i <= reviewForm.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Din recension</label>
                    <textarea
                      value={reviewForm.text}
                      onChange={e => setReviewForm(p => ({ ...p, text: e.target.value }))}
                      placeholder="Berätta om din upplevelse..."
                      rows={3}
                      className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                      data-testid="review-text-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleSubmitReview}
                      disabled={submitting || reviewForm.text.trim().length < 5}
                      className="btn-cta rounded-full px-6"
                      data-testid="review-submit-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {submitting ? 'Skickar...' : 'Skicka'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setShowReviewForm(false)}
                      className="rounded-full"
                    >
                      Avbryt
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => setShowReviewForm(true)}
                className="rounded-full px-8 border-2 hover:bg-amber-50 hover:border-amber-300 transition-all"
                data-testid="leave-review-btn"
              >
                <Star className="w-4 h-4 mr-2 text-amber-500" />
                Lämna en recension
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 md:py-28 px-6 bg-gradient-to-b from-slate-50 to-white" data-testid="pricing-section">
        <div className="max-w-xl mx-auto text-center">
          <p className="text-sm tracking-widest uppercase text-violet-500 font-medium mb-4">Prissättning</p>
          <h2 className="text-4xl md:text-5xl font-heading font-normal tracking-tight mb-4">
            Enkel prissättning
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            En engångsbetalning. Full tillgång.
          </p>
          
          <div className="bg-card rounded-3xl p-8 md:p-10 shadow-xl border border-border/30 relative overflow-hidden">
            {/* Decorative gradient corner */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-violet-100/60 to-transparent rounded-bl-full" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-rose-100/40 to-transparent rounded-tr-full" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 text-emerald-700 text-sm font-medium mb-5">
                <Sparkles className="w-4 h-4" />
                3 dagar gratis först
              </div>
              
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-6xl font-heading font-bold text-foreground">299</span>
                <span className="text-xl text-muted-foreground ml-1">kr</span>
              </div>
              <p className="text-muted-foreground mb-8">Engångsbetalning · full tillgång</p>
              
              {/* Feature checklist */}
              <div className="text-left max-w-xs mx-auto mb-8 space-y-3">
                {['Alla funktioner inkluderade', 'Dagbok, träning, mål & mer', 'Köp en gång — använd när du vill', 'Inga dolda avgifter'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/auth')}
                className="w-full btn-cta rounded-full py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                data-testid="pricing-cta-btn"
              >
                Starta din gratis provperiod
              </Button>
              
              <p className="text-sm text-muted-foreground mt-4 flex items-center justify-center gap-2">
                <Shield className="w-4 h-4 text-emerald-500" />
                Säker betalning via Stripe
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 px-6" data-testid="faq-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-heading font-normal tracking-tight mb-4">
              Vanliga frågor
            </h2>
          </div>
          
          <div className="bg-card rounded-2xl p-6 md:p-8 border border-border/30 shadow-sm">
            {FAQ_DATA.map((item, index) => (
              <FAQItem key={index} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA - colorful gradient */}
      <section className="py-20 md:py-28 px-6 bg-gradient-to-br from-slate-800 via-blue-900 to-slate-900 relative overflow-hidden" data-testid="final-cta-section">
        {/* Decorative orbs */}
        <div className="absolute top-10 left-10 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-rose-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-violet-500/5 rounded-full blur-3xl" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-heading font-normal tracking-tight text-white mb-5">
            Redo att ta kontroll?
          </h2>
          <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">
            Börja din 3 dagars gratis provperiod idag.
          </p>
          <Button 
            onClick={() => navigate('/auth')}
            className="bg-white text-slate-900 hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            data-testid="final-cta-btn"
          >
            Kom igång nu - det är gratis
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border/30">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-3">
            <span className="text-xs text-muted-foreground/50">© {new Date().getFullYear()} Livon®</span>
            <span className="text-xs text-muted-foreground/30">·</span>
            <a href="mailto:support@livon.se" className="text-xs text-muted-foreground/50 hover:text-primary transition-colors">support@livon.se</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
